import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import SchedulerService from "../../services/SchedulerService";
import NotificationTemplateService from "../../services/NotificationTemplateService";
import HttpService from "../../services/HttpService";
import { useChosenEmployer } from "../../contexts/EmployerContext";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMemo, useState } from "react";
import RenderOnRole from "../../components/RenderOnRole";
import SchedulerTemplateDetail from "./SchedulerTemplateDetail";

const EASTERN_TIME_ZONE = "America/New_York";
const formatEasternDateTime = (value) => {
  if (!value) return "—";

  const timestamp = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value)
    ? value
    : `${value}Z`;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(date);
};

const formatEasternHour = (hourUtc) => {
  const date = new Date();
  const utcDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      hourUtc
    )
  );
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIME_ZONE,
    hour: "numeric",
    hourCycle: "h23",
    timeZoneName: "short",
  }).formatToParts(utcDate);
  return `${parts.find((part) => part.type === "hour").value}:00 ${parts.find((part) => part.type === "timeZoneName").value}`;
};

const STATUS_COLORS = {
  SCHEDULED: "info",
  ACTIVE: "success",
  COMPLETED: "default",
  CANCELLED: "error",
};

const INSTANCE_STATUS_COLORS = {
  ACTIVE: "success",
  COMPLETED: "default",
  CANCELLED: "error",
  PAUSED: "warning",
  TRANSITIONED: "info",
};

const INSTANCE_STATUSES = [
  "ALL",
  "ACTIVE",
  "COMPLETED",
  "CANCELLED",
  "PAUSED",
  "TRANSITIONED",
];

const EXEC_STATUS_COLORS = {
  PENDING: "info",
  SENT: "success",
  FAILED: "error",
  SKIPPED: "warning",
  CANCELLED: "default",
};

export default function SchedulerCampaigns() {
  const queryClient = useQueryClient();

  const { chosenEmployerId } = useChosenEmployer();
  const isAnyEmployer = chosenEmployerId === "ANY";

  const [createDlgOpen, setCreateDlgOpen] = useState(false);
  const [viewTemplate, setViewTemplate] = useState(null); // template row shown in the template detail dialog
  const [instancesCampaign, setInstancesCampaign] = useState(null); // campaign whose instances/executions are shown
  const [instancesPagination, setInstancesPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [instancesStatus, setInstancesStatus] = useState("ALL");
  const [instancesEmployeeFilter, setInstancesEmployeeFilter] = useState("");
  const [selectedInstance, setSelectedInstance] = useState(null); // instance row whose executions are shown below
  const [form, setForm] = useState({
    name: "",
    idScheduleTemplate: "",
    startDate: "",
    idEmployer: null, // only used when isAnyEmployer
    filterStatus: "", // pre_authorized_members.status — blank = any
    filterDateFrom: "", // pre_authorized_members.created_at >= this date
    filterDateTo: "", // pre_authorized_members.created_at <= this date
  });

  const filterEmployerId = isAnyEmployer ? form.idEmployer : undefined;
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showFeedback = (type, message) =>
    setFeedback({ open: true, type, message });

  // ── Employer list (only for ANY users) ───────────────────────────────
  const { data: employers = [] } = useQuery(
    ["employers-all"],
    () =>
      HttpService.getAxiosClient()
        .get(window.API_URL + "/employer/all?excludeRelations=true")
        .then((r) => r.data),
    { enabled: isAnyEmployer }
  );

  // ── Templates for picker ──────────────────────────────────────────────
  const { data: templates } = useQuery(
    ["scheduler-templates", form.idEmployer],
    () => SchedulerService.getTemplates(undefined),
    {
      select: (r) => {
        const filtered = (r.data || []).filter(
          (t) =>
            t.scheduleType.startsWith("ONBOARDING") && t.status === "ACTIVE"
        );
        console.log("Backend returned:", r.data);
        console.log("Filtered templates:", filtered);
        return filtered;
      },
      enabled: !isAnyEmployer || !!form.idEmployer,
    }
  );

  // ── Campaigns list ────────────────────────────────────────────────────
  const { data: campaigns, isLoading } = useQuery(
    ["scheduler-campaigns"],
    () => SchedulerService.getCampaigns(),
    { select: (r) => r.data }
  );

  // ── All templates (any employer) — used to show names on the campaigns grid ──
  const { data: allTemplatesById = {} } = useQuery(
    ["scheduler-templates-all"],
    () => SchedulerService.getTemplates("ANY"),
    {
      select: (r) =>
        Object.fromEntries(
          (r.data || []).map((t) => [t.idScheduleTemplate, t])
        ),
    }
  );

  // ── Pre-authorized member statuses (for the enrolment filter picker) ──
  const { data: preauthStatuses = [] } = useQuery(
    ["preauth-statuses", filterEmployerId],
    () => SchedulerService.getPreauthorizedStatuses(filterEmployerId),
    {
      select: (r) => r.data || [],
      enabled: !isAnyEmployer || !!form.idEmployer,
    }
  );

  // ── Live count of members matching the current filters ────────────────
  const { data: preauthPreviewCount, isFetching: isFetchingPreview } = useQuery(
    [
      "preauth-preview",
      filterEmployerId,
      form.filterStatus,
      form.filterDateFrom,
      form.filterDateTo,
    ],
    () =>
      SchedulerService.getPreauthorizedPreview({
        employerId: filterEmployerId,
        status: form.filterStatus || undefined,
        dateFrom: form.filterDateFrom || undefined,
        dateTo: form.filterDateTo || undefined,
      }),
    {
      select: (r) => r.data?.count,
      enabled: createDlgOpen && (!isAnyEmployer || !!form.idEmployer),
    }
  );

  // ── Instances for the campaign whose "Instances" dialog is open ────────
  const { data: instancesData, isFetching: isFetchingInstances } = useQuery(
    [
      "campaign-instances",
      instancesCampaign?.idScheduleCampaign,
      instancesStatus,
      instancesEmployeeFilter,
      instancesPagination,
    ],
    () =>
      SchedulerService.getInstances({
        campaignId: instancesCampaign.idScheduleCampaign,
        employerId: instancesCampaign.idEmployer,
        status: instancesStatus,
        idEmployee: instancesEmployeeFilter || undefined,
        page: instancesPagination.page,
        pageSize: instancesPagination.pageSize,
      }),
    {
      select: (r) => r.data,
      enabled: !!instancesCampaign,
    }
  );

  const { data: instanceStats, isFetching: isFetchingInstanceStats } = useQuery(
    ["campaign-instance-stats", instancesCampaign?.idScheduleCampaign],
    () =>
      SchedulerService.getCampaignInstanceStats(
        instancesCampaign.idScheduleCampaign
      ).then((r) => r.data),
    { enabled: !!instancesCampaign }
  );

  // ── Executions for the instance selected within that dialog ────────────
  const { data: selectedInstanceExecutions, isFetching: isFetchingExecutions } =
    useQuery(
      ["campaign-instance-executions", selectedInstance?.idInstance],
      () => SchedulerService.getExecutions(selectedInstance.idInstance),
      {
        select: (r) => r.data,
        enabled: !!selectedInstance,
      }
    );

  // ── Task details via template days (mirrors SchedulerInstances) ────────
  const { data: selectedInstanceDays } = useQuery(
    ["campaign-instance-days", selectedInstance?.idScheduleTemplate],
    () => SchedulerService.getDays(selectedInstance.idScheduleTemplate),
    { enabled: !!selectedInstance, select: (r) => r.data }
  );
  const dayTaskResults = useQueries({
    queries: (selectedInstanceDays || []).map((day) => ({
      queryKey: ["scheduler-tasks-day", day.idScheduleDay],
      queryFn: () =>
        SchedulerService.getTasks(day.idScheduleDay).then((r) => r.data),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const taskMap = useMemo(() => {
    const map = {};
    dayTaskResults.forEach(({ data }) => {
      (data || []).forEach((task) => {
        map[task.idScheduleTask] = task;
      });
    });
    return map;
  }, [dayTaskResults]);

  // ── Notification templates for the "Preview" button ─────────────────
  const [previewProcess, setPreviewProcess] = useState(null); // idProcess string
  const { data: previewTemplates, isLoading: previewLoading } = useQuery(
    ["notif-templates-process", previewProcess],
    () =>
      NotificationTemplateService.getByProcess(previewProcess).then(
        (r) => r.data
      ),
    { enabled: !!previewProcess }
  );

  const createMutation = useMutation(
    (body) =>
      SchedulerService.createCampaign({
        ...body,
        startDate: body.startDate,
        filterStatus: body.filterStatus || undefined,
        filterDateFrom: body.filterDateFrom || undefined,
        filterDateTo: body.filterDateTo || undefined,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scheduler-campaigns"]);
        setCreateDlgOpen(false);
        setForm({
          name: "",
          idScheduleTemplate: "",
          startDate: "",
          idEmployer: null,
          filterStatus: "",
          filterDateFrom: "",
          filterDateTo: "",
        });
        showFeedback(
          "success",
          "Campaign created — Temporal workflow will fire at start date"
        );
      },
      onError: () => showFeedback("error", "Failed to create campaign"),
    }
  );

  const columns = [
    {
      field: "name",
      headerName: "Campaign",
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Typography variant="body2" noWrap title={row.name}>
              {row.name}
            </Typography>
          </Box>
          {isAnyEmployer && (
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              display="block"
              noWrap
              fontSize={12}
            >
              {employers.find((e) => e.idEmployer === row.idEmployer)?.name ||
                row.idEmployer}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "idScheduleTemplate",
      headerName: "Template",
      width: 400,
      renderCell: ({ value }) => {
        const template = allTemplatesById[value];
        return (
          <Box display="flex" alignItems="center" gap={0.5} width="100%">
            <Typography variant="body2" noWrap title={template?.name || value}>
              {template?.name || value}
            </Typography>
            <Tooltip title="Open template">
              <IconButton
                size="small"
                onClick={() =>
                  setViewTemplate({ idScheduleTemplate: value, ...template })
                }
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 210,
      renderCell: ({ value }) =>
        value ? new Date(value).toLocaleString() : "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          color={STATUS_COLORS[value] || "default"}
        />
      ),
    },
    {
      field: "creationDate",
      headerName: "Created",
      width: 170,
      renderCell: ({ value }) =>
        value ? new Date(value).toLocaleDateString() : "—",
    },
    {
      field: "instances",
      headerName: "Instances",
      width: 200,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: ({ row }) => (
        <Tooltip title="View enrolled instances">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedInstance(null);
              setInstancesPagination({ page: 0, pageSize: 10 });
              setInstancesCampaign(row);
            }}
          >
            <GroupIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Onboarding Campaigns</Typography>
        <RenderOnRole roles={["scheduler-admin", "pc-program-super-admin"]}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDlgOpen(true)}
          >
            New Campaign
          </Button>
        </RenderOnRole>
      </Box>

      <DataGrid
        autoHeight
        rows={campaigns || []}
        columns={columns}
        getRowId={(r) => r.idScheduleCampaign}
        loading={isLoading}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{ toolbar: { showQuickFilter: true } }}
        rowsPerPageOptions={[10, 25, 50]}
        initialState={{
          pagination: { pageSize: 10 },
          sorting: { sortModel: [{ field: "startDate", sort: "desc" }] },
        }}
        disableSelectionOnClick
      />

      {/* ── Create campaign dialog ─── */}
      <Dialog
        open={createDlgOpen}
        onClose={() => setCreateDlgOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          New Onboarding Campaign
          <IconButton
            onClick={() => setCreateDlgOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <TextField
                label="Campaign Name"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                fullWidth
                required
              />
            </Grid>
            {isAnyEmployer && (
              <Grid item xs={12}>
                <Autocomplete
                  options={employers}
                  getOptionLabel={(e) =>
                    e.name ? `${e.name} (${e.idEmployer})` : e.idEmployer
                  }
                  value={
                    employers.find((e) => e.idEmployer === form.idEmployer) ||
                    null
                  }
                  onChange={(_, val) => {
                    setForm((f) => ({
                      ...f,
                      idEmployer: val ? val.idEmployer : null,
                      idScheduleTemplate: "", // reset template when employer changes
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employer"
                      required
                      helperText="Select the employer this campaign belongs to"
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Schedule Template</InputLabel>
                <Select
                  value={form.idScheduleTemplate}
                  label="Schedule Template"
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      idScheduleTemplate: e.target.value,
                    }))
                  }
                >
                  {(templates || []).map((t) => (
                    <MenuItem
                      key={t.idScheduleTemplate}
                      value={t.idScheduleTemplate}
                    >
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                value={form.startDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, startDate: e.target.value }))
                }
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="UTC time — the Temporal workflow will sleep until this date before enrolling members"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Member Selection (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Filters apply to pre-authorized members for the selected
                employer. Members who are already employees are always excluded.
                Leave blank to include everyone.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.filterStatus}
                  label="Status"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, filterStatus: e.target.value }))
                  }
                >
                  <MenuItem value="">Any status</MenuItem>
                  {preauthStatuses.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Enrolled From"
                type="date"
                value={form.filterDateFrom}
                onChange={(e) =>
                  setForm((f) => ({ ...f, filterDateFrom: e.target.value }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Enrolled To"
                type="date"
                value={form.filterDateTo}
                onChange={(e) =>
                  setForm((f) => ({ ...f, filterDateTo: e.target.value }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {isFetchingPreview
                  ? "Calculating matching members…"
                  : preauthPreviewCount !== undefined
                    ? `${preauthPreviewCount} member(s) will be enrolled`
                    : ""}
              </Typography>
            </Grid>
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => createMutation.mutate(form)}
              disabled={
                !form.name ||
                !form.idScheduleTemplate ||
                !form.startDate ||
                (isAnyEmployer && !form.idEmployer) ||
                createMutation.isLoading
              }
            >
              Launch Campaign
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Template detail dialog ─── */}
      <Dialog
        open={!!viewTemplate}
        onClose={() => setViewTemplate(null)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogContent>
          {viewTemplate && (
            <SchedulerTemplateDetail
              templateIdProp={viewTemplate.idScheduleTemplate}
              templateProp={viewTemplate}
              onClose={() => setViewTemplate(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Instances + Executions dialog (all in one view) ─── */}
      <Dialog
        open={!!instancesCampaign}
        onClose={() => {
          setInstancesCampaign(null);
          setSelectedInstance(null);
          setInstancesStatus("ALL");
          setInstancesEmployeeFilter("");
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" component="span" noWrap>
              Instances — {instancesCampaign?.name}
            </Typography>
            {isFetchingInstanceStats ? (
              <CircularProgress size={18} />
            ) : (
              <Tooltip title="Instances created as employees / total instances">
                <Chip
                  label={
                    <Box component="span">
                      <Box component="span" fontWeight={700}>
                        {instanceStats?.createdEmployeeInstances || 0}
                      </Box>
                      /{instanceStats?.totalInstances || 0}
                    </Box>
                  }
                  size="small"
                  color={
                    instanceStats?.totalInstances > 0 &&
                    instanceStats.createdEmployeeInstances ===
                      instanceStats.totalInstances
                      ? "success"
                      : "default"
                  }
                />
              </Tooltip>
            )}
          </Box>
          <IconButton
            onClick={() => {
              setInstancesCampaign(null);
              setSelectedInstance(null);
              setInstancesStatus("ALL");
              setInstancesEmployeeFilter("");
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="caption"
            color="text.secondary"
            mb={1}
            display="block"
          >
            Click a member below to see their scheduled/sent tasks.
          </Typography>
          <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
            {INSTANCE_STATUSES.map((status) => (
              <Chip
                key={status}
                label={status}
                onClick={() => {
                  setInstancesStatus(status);
                  setInstancesPagination((current) => ({
                    ...current,
                    page: 0,
                  }));
                  setSelectedInstance(null);
                }}
                variant={instancesStatus === status ? "filled" : "outlined"}
                color={
                  instancesStatus === status
                    ? status === "ALL"
                      ? "primary"
                      : INSTANCE_STATUS_COLORS[status] || "default"
                    : "default"
                }
                size="small"
              />
            ))}
          </Box>
          <DataGrid
            density="compact"
            rows={instancesData?.data || []}
            rowCount={instancesData?.total || 0}
            columns={[
              {
                field: "idEmployee",
                headerName: "Employee ID",
                flex: 1,
                minWidth: 180,
              },
              {
                field: "status",
                headerName: "Status",
                width: 130,
                filterable: false,
                renderCell: ({ value }) => (
                  <Chip
                    size="small"
                    label={value}
                    color={INSTANCE_STATUS_COLORS[value] || "default"}
                  />
                ),
              },
              {
                field: "anchorDate",
                headerName: "Anchor Date (Day 1)",
                width: 190,
                filterable: false,
                renderCell: ({ value }) =>
                  value ? new Date(value).toLocaleString() : "—",
              },
              {
                field: "creationDate",
                headerName: "Enrolled",
                width: 150,
                filterable: false,
                renderCell: ({ value }) =>
                  value ? new Date(value).toLocaleDateString() : "—",
              },
            ]}
            getRowId={(r) => r.idInstance}
            loading={isFetchingInstances}
            paginationMode="server"
            page={instancesPagination.page}
            pageSize={instancesPagination.pageSize}
            onPageChange={(page) =>
              setInstancesPagination((current) => ({ ...current, page }))
            }
            onPageSizeChange={(pageSize) =>
              setInstancesPagination({ page: 0, pageSize })
            }
            rowsPerPageOptions={[10, 25, 50, 100]}
            filterMode="server"
            onFilterModelChange={(filterModel) => {
              const idEmployeeFilter = filterModel.items?.find(
                (item) => item.field === "idEmployee" && item.value
              );
              setInstancesEmployeeFilter(
                idEmployeeFilter ? String(idEmployeeFilter.value) : ""
              );
              setInstancesPagination((current) => ({ ...current, page: 0 }));
            }}
            onRowClick={({ row }) => setSelectedInstance(row)}
            getRowClassName={({ row }) =>
              row.idInstance === selectedInstance?.idInstance
                ? "campaign-instance-row--selected"
                : ""
            }
            sx={{
              height: 360,
              cursor: "pointer",
              "& .campaign-instance-row--selected": {
                bgcolor: "action.selected",
              },
            }}
            disableSelectionOnClick
          />

          <Box mt={3}>
            <Typography variant="subtitle2" mb={1}>
              {selectedInstance
                ? `Executions — ${selectedInstance.idEmployee}`
                : "Executions"}
            </Typography>
            {!selectedInstance ? (
              <Typography variant="body2" color="text.secondary">
                Select a member above to see their scheduled/sent tasks.
              </Typography>
            ) : (
              <DataGrid
                autoHeight
                density="compact"
                rows={selectedInstanceExecutions || []}
                columns={[
                  { field: "idExecution", headerName: "#", width: 120 },
                  {
                    field: "dayNumber",
                    headerName: "Day",
                    width: 80,
                    type: "number",
                  },
                  {
                    field: "idScheduleTask",
                    headerName: "Task",
                    width: 340,
                    renderCell: ({ value }) => {
                      const task = taskMap[value];
                      if (!task)
                        return (
                          <code style={{ fontSize: "0.78rem" }}>{value}</code>
                        );
                      const CHANNEL_COLORS = {
                        SMS: "#0277bd",
                        EMAIL: "#6a1b9a",
                        VOICE: "#2e7d32",
                        APP_MESSAGE: "#e65100",
                      };
                      return (
                        <Tooltip
                          title={
                            <Box sx={{ fontSize: "0.8rem", lineHeight: 1.6 }}>
                              <div>
                                <strong>Task ID:</strong> {value}
                              </div>
                              <div>
                                <strong>Process:</strong> {task.idProcess}
                              </div>
                              <div>
                                <strong>Hour:</strong>{" "}
                                {formatEasternHour(task.hourUtc)}
                              </div>
                              {task.subject && (
                                <div>
                                  <strong>Subject:</strong> {task.subject}
                                </div>
                              )}
                              {task.conditionJson && (
                                <div>
                                  <strong>Condition:</strong>{" "}
                                  {task.conditionJson}
                                </div>
                              )}
                            </Box>
                          }
                          arrow
                        >
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Chip
                              size="small"
                              label={task.taskType}
                              sx={{
                                bgcolor:
                                  CHANNEL_COLORS[task.taskType] || "#555",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontSize: "0.78rem" }}
                            >
                              {task.idProcess}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                minWidth: 0,
                                px: 0.75,
                                py: 0,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewProcess(task.idProcess);
                              }}
                            >
                              Preview
                            </Button>
                          </Box>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    field: "scheduledAt",
                    headerName: "Scheduled / Executed",
                    width: 200,
                    renderCell: ({ row }) => {
                      return (
                        <Box lineHeight={1.4}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.78rem" }}
                          >
                            {formatEasternDateTime(row.scheduledAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatEasternDateTime(row.executedAt)}
                          </Typography>
                        </Box>
                      );
                    },
                  },
                  {
                    field: "status",
                    headerName: "Status",
                    width: 120,
                    renderCell: ({ value }) => (
                      <Chip
                        size="small"
                        label={value}
                        color={EXEC_STATUS_COLORS[value] || "default"}
                      />
                    ),
                  },
                  {
                    field: "channelResponse",
                    headerName: "Channel Response",
                    flex: 1,
                    minWidth: 180,
                    renderCell: ({ value }) =>
                      value ? (
                        <code style={{ fontSize: "0.75rem" }}>{value}</code>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ),
                  },
                  {
                    field: "errorMessage",
                    headerName: "Error",
                    flex: 1,
                    minWidth: 180,
                    renderCell: ({ value }) =>
                      value ? (
                        <Typography
                          variant="body2"
                          color="error"
                          noWrap
                          title={value}
                        >
                          {value}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ),
                  },
                ]}
                getRowId={(r) => r.idExecution}
                loading={isFetchingExecutions}
                rowsPerPageOptions={[10, 25, 50]}
                initialState={{
                  pagination: { pageSize: 10 },
                }}
                disableSelectionOnClick
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Notification template preview dialog ─── */}
      <Dialog
        open={!!previewProcess}
        onClose={() => setPreviewProcess(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Notification Template Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Process: <strong>{previewProcess}</strong>
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setPreviewProcess(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !previewTemplates?.length ? (
            <Typography color="text.secondary" py={2}>
              No notification templates found for this process.
            </Typography>
          ) : (
            <Stack spacing={2} mt={0.5}>
              {previewTemplates.map((t) => (
                <Box
                  key={t.idNotificationTemplate}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      size="small"
                      label={t.typeTemplate}
                      color={
                        t.typeTemplate === "SMS"
                          ? "primary"
                          : t.typeTemplate === "EMAIL"
                            ? "secondary"
                            : "default"
                      }
                    />
                    {t.lang && (
                      <Chip size="small" label={t.lang} variant="outlined" />
                    )}
                    <Chip
                      size="small"
                      label={t.status}
                      color={t.status === "ACTIVE" ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>
                  {t.idTemplate && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Template ID: <code>{t.idTemplate}</code>
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  {t.template ? (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        bgcolor: "action.hover",
                        p: 1.5,
                        borderRadius: 1,
                      }}
                    >
                      {t.template}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      (No body — uses external template ID)
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={5000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.type}
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
